/**
 * Weather-Based Task Suggestions Component for HoneyDo Platform
 * Intelligently suggests tasks based on current and forecast weather conditions
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, Badge, Alert, Button } from 'flowbite-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sun,
  CloudRain,
  AlertTriangle,
  ChevronRight,
  X,
  Calendar,
  TrendingUp
} from 'lucide-react';
import {
  calculateSuitability,
  getWeatherTaskSuggestions,
  getWeatherWarnings,
  findNextGoodDay,
  shouldSuggestIndoorTasks,
  WeatherConditions,
  TaskSuggestion
} from '../../utils/weatherSuitability';

interface WeatherTaskSuggestionsProps {
  currentWeather: WeatherConditions | null;
  forecast: WeatherConditions[];
  tasks: any[];
  onTaskFilterClick?: (category: string) => void;
  onTaskClick?: (taskId: string) => void;
  className?: string;
}

export const WeatherTaskSuggestions: React.FC<WeatherTaskSuggestionsProps> = ({
  currentWeather,
  forecast,
  tasks,
  onTaskFilterClick,
  onTaskClick,
  className = ''
}) => {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Calculate suggestions based on weather
  const suggestions = useMemo(() => {
    if (!currentWeather || forecast.length === 0) return [];

    const pendingTasks = tasks.filter(task =>
      task.status !== 'completed' && task.status !== 'Completed'
    );

    return getWeatherTaskSuggestions(currentWeather, forecast, pendingTasks);
  }, [currentWeather, forecast, tasks]);

  // Get weather warnings
  const warnings = useMemo(() => {
    if (forecast.length === 0) return [];

    const pendingOutdoorTasks = tasks.filter(task => {
      const category = task.category?.toLowerCase() || '';
      return (
        (task.status !== 'completed' && task.status !== 'Completed') &&
        (category.includes('outdoor') ||
         category.includes('landscaping') ||
         category.includes('painting') ||
         category.includes('roof'))
      );
    });

    return getWeatherWarnings(forecast, pendingOutdoorTasks);
  }, [forecast, tasks]);

  // Check if we should suggest indoor tasks
  const suggestIndoor = useMemo(() => {
    return currentWeather ? shouldSuggestIndoorTasks(currentWeather) : false;
  }, [currentWeather]);

  // Filter out dismissed suggestions
  const activeSuggestions = suggestions.filter(
    suggestion => !dismissed.has(suggestion.category)
  );

  const handleDismiss = (category: string) => {
    setDismissed(prev => new Set(Array.from(prev).concat([category])));
  };

  const handleCategoryClick = (category: string) => {
    if (expandedCategory === category) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(category);
    }
  };

  const handleViewTasks = (category: string) => {
    if (onTaskFilterClick) {
      onTaskFilterClick(category);
    }
  };

  // Don't render if no weather data
  if (!currentWeather || forecast.length === 0) {
    return null;
  }

  // Don't render if no suggestions and no warnings
  if (activeSuggestions.length === 0 && warnings.length === 0 && !suggestIndoor) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Weather Warnings */}
      <AnimatePresence>
        {warnings.map((warning, index) => (
          <motion.div
            key={`warning-${index}`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Alert
              color="warning"
              className="border-l-4 border-yellow-500"
              onDismiss={() => handleDismiss(`warning-${index}`)}
            >
              <div>
                <span className="font-medium">{warning.message}</span>
                {warning.tasks.length > 0 && (
                  <div className="mt-2 text-sm">
                    Affected tasks: {warning.tasks.slice(0, 3).map(t => t.title).join(', ')}
                    {warning.tasks.length > 3 && ` and ${warning.tasks.length - 3} more`}
                  </div>
                )}
              </div>
            </Alert>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Indoor Task Suggestion */}
      {suggestIndoor && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Alert color="info" className="border-l-4 border-blue-500">
            <span className="font-medium">
              Weather not ideal for outdoor work. Great day for indoor tasks!
            </span>
          </Alert>
        </motion.div>
      )}

      {/* Task Suggestions */}
      <AnimatePresence>
        {activeSuggestions.map((suggestion) => (
          <motion.div
            key={suggestion.category}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="relative border-l-4 border-green-500 hover:shadow-lg transition-shadow">
              {/* Dismiss button */}
              <button
                onClick={() => handleDismiss(suggestion.category)}
                className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Dismiss suggestion"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>

              <div className="pr-8">
                {/* Header */}
                <div className="flex items-start space-x-3 mb-3">
                  <div className="text-3xl mt-1">{suggestion.suitability.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Great weather for {suggestion.title.toLowerCase()}!
                      </h3>
                    </div>

                    {/* Suitability badge */}
                    <Badge
                      color={
                        suggestion.suitability.level === 'excellent' ? 'success' :
                        suggestion.suitability.level === 'good' ? 'info' : 'warning'
                      }
                      size="sm"
                      className="mb-2"
                    >
                      {suggestion.suitability.level.toUpperCase()} conditions (Score: {Math.round(suggestion.suitability.score)}/100)
                    </Badge>

                    {/* Reasoning */}
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {suggestion.suitability.reasoning}
                    </p>
                  </div>
                </div>

                {/* Matching tasks preview */}
                {suggestion.tasks && suggestion.tasks.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        You have {suggestion.tasks.length} related task{suggestion.tasks.length !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={() => handleCategoryClick(suggestion.category)}
                        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center space-x-1"
                      >
                        <span>{expandedCategory === suggestion.category ? 'Hide' : 'Show'}</span>
                        <ChevronRight
                          className={`w-4 h-4 transition-transform ${
                            expandedCategory === suggestion.category ? 'rotate-90' : ''
                          }`}
                        />
                      </button>
                    </div>

                    {/* Expanded task list */}
                    <AnimatePresence>
                      {expandedCategory === suggestion.category && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-2 mt-2">
                            {suggestion.tasks.slice(0, 5).map((task) => (
                              <div
                                key={task.id || task.task_id}
                                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                                onClick={() => onTaskClick && onTaskClick(task.id || task.task_id)}
                              >
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {task.title}
                                  </div>
                                  {task.priority && (
                                    <Badge color="gray" size="xs" className="mt-1">
                                      {task.priority} priority
                                    </Badge>
                                  )}
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </div>
                            ))}
                            {suggestion.tasks.length > 5 && (
                              <div className="text-xs text-gray-500 text-center pt-1">
                                And {suggestion.tasks.length - 5} more task{suggestion.tasks.length - 5 !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex space-x-2 mt-4">
                  <Button
                    color="success"
                    size="sm"
                    onClick={() => handleViewTasks(suggestion.category)}
                    className="flex-1"
                  >
                    <Sun className="w-4 h-4 mr-2" />
                    View {suggestion.title} Tasks
                  </Button>

                  {/* Show forecast button */}
                  <Button
                    color="gray"
                    size="sm"
                    onClick={() => {
                      const nextGood = findNextGoodDay(forecast.slice(1), suggestion.category);
                      if (nextGood) {
                        alert(`Next good day: ${nextGood.day} (Score: ${Math.round(nextGood.suitability.score)}/100)`);
                      } else {
                        alert('No ideal weather days found in forecast');
                      }
                    }}
                  >
                    <Calendar className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Summary card if multiple suggestions */}
      {activeSuggestions.length > 2 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border-green-200 dark:border-green-800">
            <div className="flex items-center space-x-3">
              <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  Excellent weather conditions today!
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Perfect opportunity to tackle {activeSuggestions.length} types of outdoor tasks. Make the most of it!
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export default WeatherTaskSuggestions;
